import { useSelector } from "react-redux";

import { ALL_CONFIG_VERTICALS } from "../../../config/constants";
import { CREDIT_HELP_PATH } from "../../../config/site/professorcredit";
import {
	ALTERNATIVE_INVESTMENTS_PATH,
	AUTO_REFINANCE_PATH,
	BILL_NEGOTIATION_PATH,
	BUSINESS_BANKING_PATH,
	BUSINESS_LOANS_DEFAULT_PATH,
	CREDIT_BUILDER_LOANS_DEFAULT_PATH,
	CREDIT_MONITORING_DEFAULT_PATH,
	CREDIT_REPAIR_DEFAULT_PATH,
	DEBT_RELIEF_DEFAULT_PATH,
	FINANCIAL_ADVISORS_PATH,
	IDENTITY_THEFT_PROTECTION_PATH,
	MORTGAGE_REFINANCE_PATH,
	PAYDAY_LOANS_PATH,
	PERSONAL_LOANS_DEFAULT_PATH,
	SHARED_EQUITY_AGREEMENTS_PATH,
	SPEND_MANAGEMENT_PATH,
	STUDENT_LOANS_REFINANCE_DEFAULT_PATH,
	TAX_PREPARATION_SERVICES_PATH,
	TAX_RELIEF_DEFAULT_PATH,
} from "../../../config/site/thecreditreview";
import { BaseTransitionWrapper } from "../../components/BaseComponents/BaseTransitionWrapper";
import { BaseVerticalCheck } from "../../components/BaseComponents/BaseVerticalCheck";
import { TheCreditReviewFilter } from "../../components/Filtering/TheCreditReviewFilter";
import { AppProviders } from "../../components/UIComponents/AppProviders";
import { BaseCompanyList } from "../../components/UIComponents/BaseCompanyList";
import { logError } from "../../helpers/logError";
import { getVertical } from "../../selectors";
import { CompanyListHeader } from "./components/CompanyListHeader";
import { AutoRefinanceCompany } from "./verticals/AutoRefinance/AutoRefinanceCompany";
import { BusinessBankingCompany } from "./verticals/BusinessBanking/BusinessBankingCompany";
import { BusinessBankingFoldPlacements } from "./verticals/BusinessBanking/BusinessBankingFoldPlacements";
import { DemoGridTCR } from "./verticals/DemoGridTCR/DemoGridTCR";
import { styles } from "./verticals/pageLayoutStyles";
import { AlternativeInvestmentsCompany } from "./verticals/TCRColumnLayout/AlternativeInvestmentsCompany";
import { BusinessLoansCompany } from "./verticals/TCRColumnLayout/BusinessLoansCompany";
import { CreditMonitoringCompany } from "./verticals/TCRColumnLayout/CreditMonitoringCompany";
import { PaydayLoansCompany } from "./verticals/TCRColumnLayout/PaydayLoansCompany";
import { SpendManagementCompany } from "./verticals/TCRColumnLayout/SpendManagementCompany";
import { TaxPreparationServicesCompany } from "./verticals/TCRColumnLayout/TaxPreparationServicesCompany";
import { TCRColumnLayout } from "./verticals/TCRColumnLayout/TCRColumnLayout";
import { TCRLegacyLayout } from "./verticals/TCRLegacyLayout/TCRLegacyLayout";

const TheCreditReviewCompany = () => {
	const vertical = useSelector(getVertical);

	if (!ALL_CONFIG_VERTICALS.includes(vertical)) {
		return <DemoGridTCR />;
	}

	const componentMap = {
		/**
		 * The Credit Review (tcr)
		 */
		// http://local.thecreditreview.com/alternative-investments
		[ALTERNATIVE_INVESTMENTS_PATH]: AlternativeInvestmentsCompany,
		// http://local.thecreditreview.com/auto-refinance/
		[AUTO_REFINANCE_PATH]: AutoRefinanceCompany,
		// http://local.thecreditreview.com/bill-negotiation
		[BILL_NEGOTIATION_PATH]: CreditMonitoringCompany,
		// http://local.thecreditreview.com/business-banking
		[BUSINESS_BANKING_PATH]: BusinessBankingCompany,
		// http://local.thecreditreview.com/business-loans
		[BUSINESS_LOANS_DEFAULT_PATH]: BusinessLoansCompany,
		// http://local.thecreditreview.com/credit-builder-loans
		[CREDIT_BUILDER_LOANS_DEFAULT_PATH]: TCRLegacyLayout,
		// http://local.thecreditreview.com/credit-monitoring
		[CREDIT_MONITORING_DEFAULT_PATH]: CreditMonitoringCompany,
		// http://local.thecreditreview.com/credit-repair
		[CREDIT_REPAIR_DEFAULT_PATH]: TCRLegacyLayout,
		// http://local.thecreditreview.com/debt-relief
		[DEBT_RELIEF_DEFAULT_PATH]: TCRLegacyLayout,
		// http://local.thecreditreview.com/financial-advisors
		[FINANCIAL_ADVISORS_PATH]: TCRLegacyLayout,
		// http://local.thecreditreview.com/identity-theft-protection
		[IDENTITY_THEFT_PROTECTION_PATH]: CreditMonitoringCompany,
		// http://local.thecreditreview.com/mortgage-refinance
		[MORTGAGE_REFINANCE_PATH]: TCRColumnLayout,
		// http://local.thecreditreview.com/payday-loans
		[PAYDAY_LOANS_PATH]: PaydayLoansCompany,
		// http://local.thecreditreview.com/personal-loans
		[PERSONAL_LOANS_DEFAULT_PATH]: TCRColumnLayout,
		// http://local.thecreditreview.com/shared-equity-agreements
		[SHARED_EQUITY_AGREEMENTS_PATH]: CreditMonitoringCompany,
		// http://local.thecreditreview.com/spend-management
		[SPEND_MANAGEMENT_PATH]: SpendManagementCompany,
		// http://local.thecreditreview.com/student-loan-refinance
		[STUDENT_LOANS_REFINANCE_DEFAULT_PATH]: TCRColumnLayout,
		// http://local.thecreditreview.com/tax-preparation-services
		[TAX_PREPARATION_SERVICES_PATH]: TaxPreparationServicesCompany,

		// http://local.thecreditreview.com/tax-relief
		[TAX_RELIEF_DEFAULT_PATH]: TCRLegacyLayout,

		/**
		 * Professor Credit (pc)
		 */
		// http://local-help.professorcredit.com/credit-help
		[CREDIT_HELP_PATH]: TCRLegacyLayout,
	};

	const Component = componentMap[vertical];
	if (!Component) {
		logError(`Missing component for vertical: ${vertical}`);
		return <DemoGridTCR />;
	}

	return <Component />;
};

export const App = () => (
	<AppProviders>
		<TheCreditReviewFilter />

		<BaseTransitionWrapper>
			<BaseCompanyList
				styles={styles}
				companyListSlot={
					<>
						<BaseVerticalCheck vertical={BUSINESS_BANKING_PATH}>
							<BusinessBankingFoldPlacements />
						</BaseVerticalCheck>
						<CompanyListHeader />
					</>
				}
			>
				<TheCreditReviewCompany />
			</BaseCompanyList>
		</BaseTransitionWrapper>
	</AppProviders>
);
